import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Receipt, Fuel, Wrench, Shield, Coins, Download, ExternalLink, Plus, Trash2, Loader2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { API } from "@/lib/api";

/**
 * DriverTaxCenter — embedded inside DriverEarnings. Drivers see auto-tracked
 * business miles, can log expenses (gas/maintenance/insurance/tolls), and get
 * a side-by-side comparison of IRS Standard Mileage vs. Actual Expenses with
 * the recommended method highlighted. CSV export for accountants.
 */

const CATEGORY_META = {
  gas:         { icon: Fuel,    label: "Gas",         color: "text-amber-400" },
  maintenance: { icon: Wrench,  label: "Maintenance", color: "text-orange-400" },
  insurance:   { icon: Shield,  label: "Insurance",   color: "text-blue-400" },
  tolls:       { icon: Coins,   label: "Tolls",       color: "text-purple-400" },
  other:       { icon: Receipt, label: "Other",       color: "text-zinc-400" },
};

function fmt$(v) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(v || 0);
}

export default function DriverTaxCenter({ year }) {
  const [summary, setSummary] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);

  // Form state
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [category, setCategory] = useState("gas");
  const [amount, setAmount] = useState("");
  const [gallons, setGallons] = useState("");
  const [vendor, setVendor] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, e] = await Promise.all([
        axios.get(`${API}/driver/tax-summary`, { params: { year } }),
        axios.get(`${API}/driver/expenses`, { params: { year } }),
      ]);
      setSummary(s.data);
      setExpenses(e.data || []);
    } catch (err) {
      toast.error("Failed to load tax summary");
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const submit = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast.error("Amount must be greater than 0");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/driver/expenses`, {
        date,
        category,
        amount: parseFloat(amount),
        gallons: gallons ? parseFloat(gallons) : null,
        vendor: vendor.trim() || null,
        notes: notes.trim() || null,
      });
      toast.success("Expense logged");
      setAmount(""); setGallons(""); setVendor(""); setNotes("");
      setFormOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to log expense");
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this expense?")) return;
    try {
      await axios.delete(`${API}/driver/expenses/${id}`);
      toast.success("Deleted");
      fetchAll();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const downloadCsv = () => {
    const url = `${API}/driver/tax-summary/export?year=${year}`;
    const token = localStorage.getItem("link_token") || localStorage.getItem("token");
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        const u = URL.createObjectURL(blob);
        a.href = u;
        a.download = `link_driver_taxes_${year}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(u);
      })
      .catch(() => toast.error("Download failed"));
  };

  if (loading || !summary) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 text-[#4D88FF] animate-spin" />
      </div>
    );
  }

  const stdWinning = summary.recommended_method === "standard_mileage";

  return (
    <section className="space-y-4" data-testid="driver-tax-center">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-bold text-white">Tax Center · {year}</h2>
          <p className="text-[11px] text-zinc-500">Auto-tracked miles + your logged expenses</p>
        </div>
        <Button
          size="sm"
          variant="outline"
          className="border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10"
          onClick={downloadCsv}
          data-testid="tax-csv-download-btn"
        >
          <Download className="w-3.5 h-3.5 mr-1.5" /> CSV
        </Button>
      </div>

      {/* Side-by-side comparison */}
      <div className="grid grid-cols-2 gap-3">
        <Card
          className={`bg-[#0a0a0a] border ${stdWinning ? "border-emerald-500/50 ring-1 ring-emerald-500/30" : "border-white/10"}`}
          data-testid="std-mileage-card"
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Standard Mileage</p>
              {stdWinning && <span className="text-[9px] uppercase tracking-wider text-emerald-400 font-bold">Best</span>}
            </div>
            <p className="text-2xl font-black text-white" style={{ fontFamily: "Outfit, system-ui" }}>
              {fmt$(summary.standard_mileage_deduction)}
            </p>
            <p className="text-[11px] text-zinc-500 mt-1">
              {summary.business_miles} mi × ${summary.standard_mileage_rate_per_mile.toFixed(2)}/mi
            </p>
          </CardContent>
        </Card>

        <Card
          className={`bg-[#0a0a0a] border ${!stdWinning ? "border-emerald-500/50 ring-1 ring-emerald-500/30" : "border-white/10"}`}
          data-testid="actual-expenses-card"
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Actual Expenses</p>
              {!stdWinning && <span className="text-[9px] uppercase tracking-wider text-emerald-400 font-bold">Best</span>}
            </div>
            <p className="text-2xl font-black text-white" style={{ fontFamily: "Outfit, system-ui" }}>
              {fmt$(summary.actual_expenses_deduction)}
            </p>
            <p className="text-[11px] text-zinc-500 mt-1">
              {summary.expense_count} receipt{summary.expense_count === 1 ? "" : "s"} this year
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Net summary */}
      <Card className="bg-[#0a0a0a] border-white/10">
        <CardContent className="p-4 grid grid-cols-3 gap-3 text-center">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Gross payouts</p>
            <p className="text-lg font-bold text-white">{fmt$(summary.gross_fares)}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Best deduction</p>
            <p className="text-lg font-bold text-emerald-300">−{fmt$(summary.best_deduction_amount)}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Net taxable</p>
            <p className="text-lg font-bold text-white">{fmt$(summary.net_taxable_estimate)}</p>
          </div>
        </CardContent>
      </Card>

      {/* Per-category breakdown */}
      <Card className="bg-[#0a0a0a] border-white/10">
        <CardContent className="p-4 space-y-2">
          <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold mb-2">Expenses by category</p>
          {Object.entries(summary.expenses_by_category).map(([cat, amt]) => {
            const meta = CATEGORY_META[cat] || CATEGORY_META.other;
            const Icon = meta.icon;
            return (
              <div key={cat} className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2 text-zinc-300">
                  <Icon className={`w-3.5 h-3.5 ${meta.color}`} />
                  {meta.label}
                </span>
                <span className="text-white font-mono">{fmt$(amt)}</span>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Add expense form */}
      <Card className="bg-[#0a0a0a] border-white/10">
        <CardContent className="p-4">
          {!formOpen ? (
            <div className="flex gap-2">
              <Button
                onClick={() => setFormOpen(true)}
                className="flex-1 bg-[#0047FF] hover:bg-[#003BE0] text-white"
                data-testid="add-expense-btn"
              >
                <Plus className="w-4 h-4 mr-1" /> Log expense
              </Button>
              <a
                href="https://upside.app.link/cashback"
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-1.5 px-3 py-2 rounded-md border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 text-sm font-medium"
                data-testid="upside-btn"
              >
                <ExternalLink className="w-3.5 h-3.5" /> Upside (gas cashback)
              </a>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-zinc-400 text-xs">Date</Label>
                  <Input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className="bg-zinc-900 border-white/10 text-white mt-1"
                    data-testid="exp-date"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs">Category</Label>
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="w-full bg-zinc-900 border border-white/10 rounded-md text-white text-sm h-10 px-3 mt-1"
                    data-testid="exp-category"
                  >
                    {Object.entries(CATEGORY_META).map(([k, v]) => (
                      <option key={k} value={k}>{v.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-zinc-400 text-xs">Amount ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="45.00"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="bg-zinc-900 border-white/10 text-white mt-1"
                    data-testid="exp-amount"
                  />
                </div>
                {category === "gas" && (
                  <div>
                    <Label className="text-zinc-400 text-xs">Gallons (optional)</Label>
                    <Input
                      type="number"
                      step="0.001"
                      min="0"
                      placeholder="12.5"
                      value={gallons}
                      onChange={(e) => setGallons(e.target.value)}
                      className="bg-zinc-900 border-white/10 text-white mt-1"
                      data-testid="exp-gallons"
                    />
                  </div>
                )}
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">Vendor (optional)</Label>
                <Input
                  type="text"
                  placeholder="Sheetz, Wawa, Geico…"
                  value={vendor}
                  onChange={(e) => setVendor(e.target.value)}
                  className="bg-zinc-900 border-white/10 text-white mt-1"
                  data-testid="exp-vendor"
                />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">Notes (optional)</Label>
                <Input
                  type="text"
                  placeholder="Anything for your records"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="bg-zinc-900 border-white/10 text-white mt-1"
                  data-testid="exp-notes"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={submit}
                  disabled={submitting}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white"
                  data-testid="exp-save-btn"
                >
                  {submitting ? "Saving…" : "Save expense"}
                </Button>
                <Button
                  onClick={() => setFormOpen(false)}
                  variant="outline"
                  className="border-white/10 text-zinc-400"
                  data-testid="exp-cancel-btn"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Expense list */}
      {expenses.length > 0 && (
        <Card className="bg-[#0a0a0a] border-white/10">
          <CardContent className="p-0">
            <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold px-4 pt-4 pb-2">
              Recent expenses
            </p>
            <div className="divide-y divide-white/5">
              {expenses.map((r) => {
                const meta = CATEGORY_META[r.category] || CATEGORY_META.other;
                const Icon = meta.icon;
                return (
                  <div key={r.id} className="flex items-center gap-3 px-4 py-2.5" data-testid={`exp-row-${r.id}`}>
                    <Icon className={`w-4 h-4 flex-shrink-0 ${meta.color}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-semibold text-white">{fmt$(r.amount)}</p>
                        <p className="text-[11px] text-zinc-500">{r.date}</p>
                      </div>
                      <p className="text-[11px] text-zinc-500 truncate">
                        {meta.label}
                        {r.vendor ? ` · ${r.vendor}` : ""}
                        {r.gallons ? ` · ${r.gallons} gal` : ""}
                        {r.notes ? ` · ${r.notes}` : ""}
                      </p>
                    </div>
                    <button
                      onClick={() => remove(r.id)}
                      className="text-zinc-500 hover:text-rose-400 transition-colors"
                      data-testid={`exp-delete-${r.id}`}
                      aria-label="Delete expense"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </section>
  );
}
